import { Link } from "react-router-dom";
import { fetchDashboardDataFromSheet } from "../../api/sheetsApi";
import { useEffect, useMemo, useState } from "react";
import {
  Bus,
  Building2,
  CalendarDays,
  ClipboardList,
  Download,
  FileSpreadsheet,
  GraduationCap,
  Percent,
  SquarePen,
  UserCheck,
  UserRound,
  Users,
  Wallet,
} from "lucide-react";

const statStyles = [
  { icon: Users, color: "bg-pink-100 text-pink-600" },
  { icon: Building2, color: "bg-sky-100 text-sky-600" },
  { icon: ClipboardList, color: "bg-indigo-100 text-indigo-600" },
  { icon: ClipboardList, color: "bg-purple-100 text-purple-600" },
  { icon: GraduationCap, color: "bg-red-100 text-red-600" },
  { icon: UserRound, color: "bg-orange-100 text-orange-600" },
  { icon: Users, color: "bg-yellow-100 text-yellow-600" },
  { icon: Bus, color: "bg-cyan-100 text-cyan-600" },
  { icon: Bus, color: "bg-teal-100 text-teal-600" },
  { icon: UserCheck, color: "bg-amber-100 text-amber-600" },
  { icon: Percent, color: "bg-red-100 text-red-600" },
  { icon: Wallet, color: "bg-lime-100 text-lime-600" },
  { icon: FileSpreadsheet, color: "bg-emerald-100 text-emerald-600" },
];

const hiddenEvents = new Set(["Academic Performance"]);

const parseNumber = (value) => {
  const parsed = Number(String(value || "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const clampPercent = (value) => `${Math.min(Math.max(value, 0), 100).toFixed(1)}%`;

const toPercentWidth = (value) => {
  if (typeof value === "string" && value.trim().endsWith("%")) {
    return value;
  }

  return clampPercent(parseNumber(value));
};

const ProgressBar = ({ label, value, width, color }) => {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">{label}</span>
        <span className="font-semibold text-gray-700">{value}</span>
      </div>

      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full ${color}`}
          style={{ width }}
        />
      </div>
    </div>
  );
};

export default function PerumbakkamDashboard() {
  const [campusData, setCampusData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState("May 2026");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchPerumbakkamCampusData = async () => {
      try {
        const data = await fetchDashboardDataFromSheet();
        setCampusData(data?.perumbakkamModelData || []);
        setCurrentMonth(data?.currentMonth || "May 2026");
      } catch (err) {
        console.error(err);
        setError(err.message || "Unable to load Perumbakkam campus data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchPerumbakkamCampusData();
  }, []);

  const campusMap = useMemo(
    () =>
      campusData.reduce((acc, item) => {
        acc[item.events] = item.summary || "0";
        return acc;
      }, {}),
    [campusData]
  );

  const stats = useMemo(
    () =>
      campusData
        .filter((item) => !hiddenEvents.has(item.events))
        .map((item, index) => ({
          title: item.events,
          value: item.summary || "0",
          ...statStyles[index % statStyles.length],
        })),
    [campusData]
  );

  const staffData = useMemo(
    () => [
      { label: "Teachers", value: campusMap["Total Teachers"] || 0, color: "bg-violet-500" },
      { label: "Admin", value: campusMap["Total Admin Staff"] || 0, color: "bg-red-500" },
      {
        label: "Housekeeping",
        value: campusMap["Total Housekeeping Staff"] || 0,
        color: "bg-yellow-500",
      },
      { label: "Drivers", value: campusMap["Total Drivers"] || 0, color: "bg-green-500" },
    ],
    [campusMap]
  );

  const metrics = useMemo(() => {
    const students = parseNumber(campusMap["Total Students"]);
    const capacity = parseNumber(campusMap["Total Capacity"]);
    const classrooms = parseNumber(campusMap["Total Classrooms"]);
    const vacantClassrooms = parseNumber(campusMap["Total Vacant Classrooms"]);
    const busRoutes = parseNumber(campusMap["Total Bus Routes"]);
    const capacityUtilised = capacity ? clampPercent((students / capacity) * 100) : "0.0%";
    const classroomUsage = classrooms
      ? clampPercent(((classrooms - vacantClassrooms) / classrooms) * 100)
      : "0.0%";
    const busCoverage = classrooms ? clampPercent((busRoutes / classrooms) * 100) : "0.0%";

    return [
      {
        label: "Capacity Utilization",
        value: capacityUtilised,
        width: capacityUtilised,
        color: "bg-pink-500",
      },
      {
        label: "Fee Collection",
        value: campusMap["Term I Fees Collected %"] || "0%",
        width: toPercentWidth(campusMap["Term I Fees Collected %"]),
        color: "bg-blue-600",
      },
      {
        label: "TC Percentage",
        value: campusMap["Total TC Percentage 25-26"] || campusMap["Total TC Percentage"] || "0%",
        width: toPercentWidth(campusMap["Total TC Percentage 25-26"] || campusMap["Total TC Percentage"]),
        color: "bg-red-500",
      },
      {
        label: "Bus Coverage",
        value: busCoverage,
        width: busCoverage,
        color: "bg-yellow-500",
      },
      {
        label: "Classroom Usage",
        value: classroomUsage,
        width: classroomUsage,
        color: "bg-green-500",
      },
    ];
  }, [campusMap]);

  if (loading) {
    return (
      <div className="p-2 bg-gray-100 min-h-[100dvh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-[#f5f6fb] p-4">
        <div className="rounded-xl bg-white px-5 py-4 text-sm font-medium text-red-500 shadow-sm">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 bg-gray-100 min-h-[100dvh] space-y-6">
      <div className="flex">
        <div className="h-28 w-36 overflow-hidden shadow-lg">
          <img
            src="/SchoolImage.png"
            alt="school"
            className="h-full w-full object-cover"
          />
        </div>

        <div className="relative flex-1 overflow-hidden rounded-r-2xl bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 p-6 text-white shadow-lg">
          <div className="absolute right-10 top-0 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-0 left-1/3 h-24 w-24 rounded-full bg-pink-400/20 blur-2xl" />

          <div className="relative z-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold">Perumbakkam Campus</h1>

              <p className="mt-1 text-sm text-purple-100">
                School Performance Overview and Analytics
                <span className="mx-2">|</span>
                Academic Year 2025-26
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button className="flex items-center justify-center gap-2 w-38 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur transition hover:bg-white/20">
                <CalendarDays size={16} />
                <span className="text-base">{currentMonth}</span>
              </button>

              <button data-html2canvas-ignore="true" className="flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur transition hover:bg-white/20">
                <Download size={16} />
                Export
              </button>

              <Link data-html2canvas-ignore="true" to={"/edit/perumbakkam"} className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-purple-700 transition hover:bg-purple-50">
  <SquarePen size={16} />
  Edit
</Link>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {stats.map((item) => {
          const Icon = item.icon;

          return (
            <div
              key={item.title}
              className="rounded-3xl bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-500">{item.title}</p>

                  <h3 className="mt-2 break-words text-2xl font-bold text-gray-800">
                    {item.value}
                  </h3>
                </div>

                <div className={`rounded-2xl p-3 ${item.color}`}>
                  <Icon size={16} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
        <div className="rounded-3xl bg-white p-5 shadow-sm">
          <h3 className="mb-5 text-lg font-semibold text-gray-800">
            Staff Breakdown
          </h3>

          <div className="flex flex-col items-center gap-5 md:flex-row">
            <div className="relative h-44 w-44">
              <div className="absolute inset-0 rounded-full border-[14px] border-sky-500 border-r-red-500 border-b-yellow-400 border-l-lime-500" />

              <div className="absolute inset-6 flex flex-col items-center justify-center rounded-full bg-white">
                <h3 className="text-3xl font-bold text-slate-800">100%</h3>
                <p className="text-xs text-gray-500">TOTAL</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              {staffData.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-2"
                >
                  <div className={`h-3 w-3 rounded-full ${item.color}`} />
                  {item.label} {item.value}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-white p-5 shadow-sm">
          <h3 className="mb-5 text-lg font-semibold text-gray-800">
            Campus Metrics
          </h3>

          <div className="space-y-5">
            {metrics.map((item) => (
              <ProgressBar
                key={item.label}
                label={item.label}
                value={item.value}
                width={item.width}
                color={item.color}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-3xl bg-white p-5 shadow-sm">
        <h3 className="mb-5 text-lg font-semibold text-gray-800">
          Transport Summary
        </h3>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-2xl bg-cyan-50 p-4 text-cyan-700">
            <p className="text-sm">Bus Routes</p>
            <p className="mt-2 text-2xl font-bold">{campusMap["Total Bus Routes"] || 0}</p>
          </div>

          <div className="rounded-2xl bg-lime-50 p-4 text-lime-700">
            <p className="text-sm">Drivers</p>
            <p className="mt-2 text-2xl font-bold">{campusMap["Total Drivers"] || 0}</p>
          </div>

          <div className="rounded-2xl bg-violet-50 p-4 text-violet-700">
            <p className="text-sm">Fee Collected</p>
            <p className="mt-2 text-2xl font-bold">{campusMap["Term I Fees Collected %"] || "0%"}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
